// components/CreateProjectTemplateModal.jsx
import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, GripVertical } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateProjectTemplateModal = ({ isOpen, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [formData, setFormData] = useState({
    projectTemplateName: "",
    templateDescription: "",
    defaultManagerId: "",
    isActive: true,
  });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchCurrentUser();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      console.log("Company users response:", response);
      setUsers(response.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await apiService.getMe();
      console.log("Current user response:", response);
      setCurrentUser(response.user);
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const templateData = {
        ...formData,
        ownerId: currentUser?.id,
        defaultManagerId: formData.defaultManagerId || null,
      };

      await apiService.createProjectTemplate(templateData);
      onSuccess();
      handleClose();
    } catch (err) {
      console.error("Error creating template:", err);
      setError(err.response?.data?.message || "Failed to create template");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      projectTemplateName: "",
      templateDescription: "",
      defaultManagerId: "",
      isActive: true,
    });
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            New Project Template
          </h2>
          <button
            onClick={handleClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="templateForm">
            <div className="space-y-4">
              {/* Template Name */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Template Name
                </label>
                <input
                  type="text"
                  value={formData.projectTemplateName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      projectTemplateName: e.target.value,
                    })
                  }
                  required
                  placeholder="e.g., Install Type 1, Upgrade, Maintenance"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* Description */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Description
                </label>
                <textarea
                  rows="3"
                  value={formData.templateDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      templateDescription: e.target.value,
                    })
                  }
                  placeholder="Describe this project template..."
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* Owner (Read-only, shows current user) */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Owner
                </label>
                <input
                  type="text"
                  value={currentUser?.name || "Loading..."}
                  disabled
                  className={`w-full px-3 py-2 border rounded cursor-not-allowed ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-gray-400"
                      : "bg-gray-100 border-gray-300 text-gray-500"
                  }`}
                />
                <p
                  className={`mt-1 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  You will be set as the owner of this template
                </p>
              </div>

              {/* Default Manager */}
              <div>
                <label
                  className={`block text-sm mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Default Project Manager
                </label>
                <select
                  value={formData.defaultManagerId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      defaultManagerId: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Default Manager (Optional)</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                <p
                  className={`mt-1 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  This user will be assigned as project manager when using this
                  template
                </p>
              </div>

              {/* Active Status */}
              <div>
                <label
                  className={`flex items-center gap-2 text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className={`w-4 h-4 rounded border text-blue-600 focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "border-gray-700 bg-[#0C0E12]"
                        : "border-gray-300 bg-white"
                    }`}
                  />
                  Active
                </label>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={handleClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="templateForm"
            disabled={loading || !formData.projectTemplateName || !currentUser}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectTemplateModal;
